import type {DocumentNode} from 'graphql/language';
import {type MutateFunction, type MutateResult, useMutation, type UseMutationReturn} from '@vue/apollo-composable';
import {type FormKitNode, getNode, isNode} from '@formkit/core';
import type {OperationVariables} from '@apollo/client/core';
import type {Ref} from 'vue';
import {ref} from 'vue';
import {GraphQLError} from 'graphql';
import type {ReactiveFunction} from '@vue/apollo-composable/dist/util/ReactiveFunction';
import type {UseMutationOptions} from '@vue/apollo-composable/dist/useMutation';

export type MutateFunctionWithForm<TResult, TVariables> = (variables?: TVariables | null, form?: FormKitNode) => MutateResult<TResult>

export type UseSofieMutationReturn<
  TData = any,
  TVariables extends OperationVariables = OperationVariables
> = Omit<UseMutationReturn<TData, TVariables>, 'mutate'> & {
  form: Ref<FormKitNode | undefined>
  finished: Ref<boolean>
  mutate: MutateFunction<TData, TVariables> | MutateFunctionWithForm<TData, TVariables>
  validationErrors: Ref<GraphQLError[]>
}

type OptionsParameter<TResult, TVariables> = UseMutationOptions<TResult, TVariables> | Ref<UseMutationOptions<TResult, TVariables>> | ReactiveFunction<UseMutationOptions<TResult, TVariables>>;

export type UseSofieMutationOptions<
  TData = object,
  TVariables = OperationVariables
> = OptionsParameter<TData, TVariables>

export function useSofieMutation<TData = object, TVariables extends OperationVariables = OperationVariables>(
  mutation: DocumentNode,
  options: UseSofieMutationOptions<TData, TVariables> = {},
): UseSofieMutationReturn<TData, TVariables> {
  const result = useMutation<TData, TVariables>(mutation, options);
  const finished = ref(false);
  const form = ref<FormKitNode>();

  const validationErrors = ref<GraphQLError[]>([]);

  result.onError((err) => {
    validationErrors.value = [];

    err.graphQLErrors.forEach((graphqlError) => {
      if (graphqlError.extensions?.category === 'validation') {
        validationErrors.value.push(graphqlError);
        form.value?.setErrors(
          graphqlError.extensions.validation as Record<string, string>,
        );
      }
    });
  });

  result.onDone(() => {
    finished.value = true;

    form.value?.clearErrors();
    validationErrors.value = [];
  });

  return {
    ...result,
    mutate(variables: TVariables | null = null, overrideOptions: any) {
      finished.value = false;

      if (isNode(overrideOptions)) {
        form.value = overrideOptions;
        return result.mutate(variables);
      }

      return result.mutate(variables, overrideOptions);
    },
    finished,
    form,
    validationErrors,
  };
}
